import {
  DatabaseAuto,
  IAnalyticsCompanySubscription, IbulkDelete,
  IbulkDeleteSuccessResponse,
  Icompany,
  IcompanySubscription,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps, IsubscriptionPackage, validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Type alias for company subscription properties with specific generic types
 * @template T - Type for subscription amount (default: number)
 * @template S - Type for company ID (default: string)
 * @template C - Type for company details (default: Icompany)
 */
type TcompanySubscriptionProps = IcompanySubscription<number, string, Icompany>;

/**
 * Represents a company's subscription to a service or product.
 * Extends DatabaseAuto to inherit common database operations.
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link CompanySubscription} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const companySubscription = await CompanySubscription.getAll();
 *
 * const companySubscription = await CompanySubscription.filterAll({
 *
 * });
 *
 * const companySubscription = await CompanySubscription.bulkDelete({
 *
 * });
 *
 * const companySubscription = await CompanySubscription.getOne('resourceId');
 *
 * const companySubscription = await CompanySubscription.remove();
 *
 * ```
 */
export class CompanySubscription extends DatabaseAuto {
  /** Base API endpoint for company subscription operations */
  static readonly baseUrl = '/company-subscription';

  /** The name of the subscription */
  readonly name: TcompanySubscriptionProps['name'];

  /** The amount charged for the subscription */
  readonly amount: TcompanySubscriptionProps['amount'];

  /** The duration of the subscription in months */
  readonly duration: TcompanySubscriptionProps['duration'];

  /** The start date of the subscription */
  readonly startDate: TcompanySubscriptionProps['startDate'];

  /** The end date of the subscription */
  readonly endDate: TcompanySubscriptionProps['endDate'];

  /** Whether the subscription is currently active */
  readonly active: TcompanySubscriptionProps['active'];

  /** Array of features included in this subscription */
  features: TcompanySubscriptionProps['features'];

  /** The company this subscription belongs to */
  company: TcompanySubscriptionProps['company'];

  /** Additional metadata related to subscription payment */
  paymentMeta: TcompanySubscriptionProps['paymentMeta'];

  /**
   * Creates a new CompanySubscription instance with validated data.
   * @param {TcompanySubscriptionProps} data - The subscription data to initialize with
   * @throws {Error} When validation of the input data fails
   */
  constructor(data: TcompanySubscriptionProps) {
    validateData(data, {
      name: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0 &&
          v.length <= 100,
        message: 'Subscription name must be a non-empty string (max 100 characters)'
      },
      amount: {
        validate: (v) =>
          typeof v === 'number' &&
          v >= 0 &&
          v < 110000000,
        message: `Subscription amount must be a non-negative number less than
         11,000,000 (can be 0 for trial subscriptions)`
      },
      duration: {
        validate: (v) => [1, 2, 3, 6, 12].includes(v),
        message: 'Invalid subscription duration. Must be one of: 1, 2, 3, 6, or 12 months'
      },
      startDate: {
        validate: (v) => {
          // Check if the input is a valid ISO date string or a Date object
          const date = v instanceof Date ? v : new Date(v);

          return !isNaN(date.getTime());
        },
        message: 'Start date must be a valid date (ISO date string or Date object)'
      },
      endDate: {
        validate: (v) => {
          // Check if the input is a valid ISO date string or a Date object
          const date = v instanceof Date ? v : new Date(v);
          const startDate = data.startDate instanceof Date ? data.startDate : new Date(data.startDate);

          return !isNaN(date.getTime()) && date > startDate;
        },
        message: 'End date must be a valid date (ISO date string or Date object) and after start date'
      },
      features: {
        validate: (v) =>
          Array.isArray(v) &&
          v.length > 0 &&
          v.every(feature =>
            feature &&
            typeof feature === 'object' &&
            typeof feature.name === 'string' &&
            feature.name.trim().length > 0),
        message: 'Features must be a non-empty array (max 10) of valid feature objects'
      }
    });

    super(data);

    this.name = data.name;
    this.amount = data.amount;
    this.duration = data.duration;
    this.startDate = new Date(data.startDate);
    this.endDate = new Date(data.endDate);
    this.active = data.active;
    this.features = data.features;
    this.company = data.company;
    this.paymentMeta = data.paymentMeta;
  }

  /**
   * Retrieves analytics data for company subscriptions.
   * @param {IfilterProps} filter - Filter criteria for analytics
   * @returns {Promise<IAnalyticsCompanySubscription>} Analytics data for subscriptions
   */
  static getCompanySubscriptionAnalytcis(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IAnalyticsCompanySubscription>(`${CompanySubscription.baseUrl}/analytics`, filter);

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves all company subscriptions with pagination.
   * @param {number} [offset=0] - Number of records to skip
   * @param {number} [limit=20] - Maximum number of records to return
   * @returns {Promise<{count: number, dataArr: CompanySubscription[]}>} Paginated list of subscriptions
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<
        IdataArrayResponse<TcompanySubscriptionProps>>(`${CompanySubscription.baseUrl}?${query}`);
    const companySubscriptions = await lastValueFrom(observer$);

    return {
      count: companySubscriptions.count,
      dataArr: companySubscriptions.data
        .map((val) => new CompanySubscription(val)) };
  }

  /**
   * Filters company subscriptions based on provided criteria.
   * @param {IfilterProps} filter - Filter criteria for subscriptions
   * @returns {Promise<{count: number, dataArr: CompanySubscription[]}>} Filtered list of subscriptions
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TcompanySubscriptionProps>>(
        `${CompanySubscription.baseUrl}/filter`,
        filter
      );
    const companysubscriptions = await lastValueFrom(observer$);

    return {
      count: companysubscriptions.count,
      dataArr: companysubscriptions.data
        .map((val) => new CompanySubscription(val)) };
  }

  /**
   * Subscribes a company to a subscription package.
   * @param {Partial<IsubscriptionPackage>} subscriptionPackage - The subscription package details
   * @param {string} company - The ID of the company to subscribe
   * @returns {Promise<CompanySubscription>} The created subscription
   */
  static async subscribe(subscriptionPackage: Partial<IsubscriptionPackage>, company: string) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const observer$ = CommerceClient.ehttp.makePost<IcompanySubscription<number, string, Icompany>>(
      `${CompanySubscription.baseUrl}/subscribe`,
      { subscriptionPackage, company }
    );

    return new CompanySubscription(await lastValueFrom(observer$));
  }

  /**
   * Removes multiple company subscriptions in bulk.
   * @param {IbulkDelete} payload - Criteria for bulk deletion
   * @returns {Promise<IbulkDeleteSuccessResponse>} Result of the bulk delete operation
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient
      .ehttp.makePost<IbulkDeleteSuccessResponse>(`${CompanySubscription.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Removes the current subscription from the database.
   * @returns {Promise<IdeleteOneResponse | null>} Result of the delete operation
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${CompanySubscription.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
